/** @format */

import { useState } from 'react';
import { IoClose, IoDownload } from 'react-icons/io5';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { useAuthStore } from '../../context/useAuthStore';
import { AuthButton } from '../common/AuthButton';
import TextInput from './TextInput';
import { jsPDF } from 'jspdf';
import myLogo from '../../assets/llb360.png';
import SelectInput from './SelectInput';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface ExportAsPDFModalProps {
	showModal: boolean;
	onClose: () => void;
	subjectName: string;
	subjectCode: string;
}

const deepNormalize = (input: unknown): any => {
	if (Array.isArray(input)) {
		if (input.every((i) => typeof i === 'string')) {
			const noSpaceStart = ['.', ',', '!', '?', ';', ':', ')', ']', '}'];
			return (input as string[]).reduce((acc, curr) => {
				if (!acc) return curr;
				if (acc.endsWith(' ') || curr.startsWith(' ')) return acc + curr;
				if (noSpaceStart.some((char) => curr.startsWith(char))) {
					return acc + curr;
				}
				return acc + ' ' + curr;
			}, '');
		}
		return input.map(deepNormalize);
	}
	if (input && typeof input === 'object') {
		const keys = Object.keys(input);
		if (keys.every((k) => /^\d+$/.test(k))) {
			return (
				keys
					.sort((a, b) => Number(a) - Number(b))
					// @ts-expect-error
					.map((k) => deepNormalize(input[k]))
					.join('')
					.trim()
			);
		}
		return Object.fromEntries(
			// @ts-expect-error
			keys.map((k) => [k, deepNormalize(input[k])])
		);
	}
	return input;
};

const FIELD_MAPPINGS: Record<
	string,
	{
		key: string;
		label: string;
		subMapping?: { key: string; label: string }[];
	}[]
> = {
	CP: [
		{ key: 'concept', label: 'Concept' },
		{
			key: 'statutory provision',
			label: 'Statutory Provision',
			subMapping: [
				{ key: 'act', label: 'Act' },
				{ key: 'section', label: 'Section' },
				{ key: 'provision', label: 'Provision' },
				{ key: 'year', label: 'Year' },
			],
		},
		{
			key: 'One example with explanation',
			label: 'One example with explanation',
			subMapping: [
				{ key: 'example', label: 'Example' },
				{ key: 'explanation', label: 'Explanation' },
			],
		},
		{ key: 'summary', label: 'Summary' },
	],
	SN: [
		{ key: 'title', label: 'Title' },
		{ key: 'meaning', label: 'Meaning' },
		{
			key: 'statutory provision',
			label: 'Statutory Provision',
			subMapping: [
				{ key: 'act', label: 'Act' },
				{ key: 'section', label: 'Section' },
				{ key: 'provision', label: 'Provision' },
				{ key: 'year', label: 'Year' },
			],
		},
		{ key: 'key elements', label: 'Key Elements' },
		{ key: 'exceptions', label: 'Exceptions' },
		{ key: 'examples', label: 'Examples' },
		{
			key: 'most recent landmark case',
			label: 'Landmark Case',
			subMapping: [
				{ key: 'name', label: 'Name' },
				{ key: 'facts', label: 'Facts' },
				{ key: 'judgement', label: 'Judgement' },
				{ key: 'relevance', label: 'Relevance' },
			],
		},
		{ key: 'key points', label: 'Key Points' },
		{ key: 'summary', label: 'Summary' },
	],
	EN: [
		{ key: 'title', label: 'Title' },
		{ key: 'meaning', label: 'Meaning' },
		{
			key: 'statutory provision',
			label: 'Statutory Provision',
			subMapping: [
				{ key: 'act', label: 'Act' },
				{ key: 'section', label: 'Section' },
				{ key: 'provision', label: 'Provision' },
				{ key: 'year', label: 'Year' },
			],
		},
		{ key: 'key elements', label: 'Key Elements' },
		{ key: 'exceptions', label: 'Exceptions' },
		{ key: 'examples', label: 'Examples' },
		{
			key: 'most recent landmark case',
			label: 'Landmark Case',
			subMapping: [
				{ key: 'name', label: 'Name' },
				{ key: 'facts', label: 'Facts' },
				{ key: 'judgement', label: 'Judgement' },
				{ key: 'relevance', label: 'Relevance' },
			],
		},
		{ key: 'key points', label: 'Key Points' },
		{ key: 'summary', label: 'Summary' },
	],
	CS: [
		{ key: 'title', label: 'Title' },
		{ key: 'facts_of_the_case', label: 'Facts of the Case' },
		{ key: 'facts_in_issue', label: 'Facts in Issue' },
		{
			key: 'applicable_principle',
			label: 'Applicable Principle',
			subMapping: [
				{ key: 'principle', label: 'Principle' },
				{ key: 'authority', label: 'Authority' },
				{ key: 'source', label: 'Source' },
			],
		},
		{
			key: 'recent_landmark_case',
			label: 'Landmark Case',
			subMapping: [
				{ key: 'title', label: 'Title' },
				{ key: 'facts', label: 'Facts' },
				{ key: 'held', label: 'Held' },
				{ key: 'relevance', label: 'Relevance' },
			],
		},
		{ key: 'summary', label: 'Summary' },
	],
};

// Helper: Clean text for PDF (replace unsupported characters)
const cleanTextForPdf = (text: string): string => {
	if (!text) return '';
	return text
		.replace(/₹/g, 'Rs. ')
		.replace(/[\u2018\u2019]/g, "'") // smart single quotes
		.replace(/[\u201C\u201D]/g, '"') // smart double quotes
		.replace(/–/g, '-') // en dash
		.replace(/—/g, '-') // em dash
		.replace(/…/g, '...'); // ellipsis
};

// Helper: Format key to title case (e.g. partner_action -> Partner Action)
const formatKey = (key: string): string => {
	if (!key) return '';
	// split by underscore or camelCase
	const words = key.replace(/([A-Z])/g, ' $1').split(/[_\s]+/);
	return words
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ')
		.trim();
};

const ExportAsPDFModal: React.FC<ExportAsPDFModalProps> = ({
	showModal,
	onClose,
	subjectName,
	subjectCode,
}) => {
	const [waterMark, setWaterMark] = useState<string>('');
	const [answerType, setAnswerType] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const { token, clearSession } = useAuthStore();

	const addPageDetails = (doc: jsPDF, text: string) => {
		const totalPages = doc.getNumberOfPages();
		const pageWidth = doc.internal.pageSize.getWidth();
		const pageHeight = doc.internal.pageSize.getHeight();

		for (let i = 1; i <= totalPages; i++) {
			doc.setPage(i);

			// Add Watermark
			doc.saveGraphicsState();
			doc.setGState(new (doc as any).GState({ opacity: 0.2 }));
			doc.setTextColor(160, 160, 160);
			doc.setFontSize(140);
			doc.setFont('helvetica', 'bold');
			doc.text(
				text.toLocaleUpperCase(),
				pageWidth / 2 + 50,
				pageHeight / 2 + 75,
				{
					align: 'center',
					angle: 45,
				}
			);
			doc.restoreGraphicsState();

			// Add Footer
			const footerY = pageHeight - 20;
			doc.setDrawColor(200, 200, 200);
			doc.setLineWidth(0.5);
			doc.line(15, footerY, pageWidth - 15, footerY);

			doc.setFontSize(10);
			doc.setTextColor(100, 100, 100);
			doc.setFont('helvetica', 'normal');

			// Rights (Left)
			doc.text(
				`© ${new Date().getFullYear()} LLB 360. All rights reserved.`,
				15,
				footerY + 8
			);

			// Page Number (Right)
			doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, footerY + 8, {
				align: 'right',
			});
		}
	};

	const handleExport = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!waterMark.trim()) {
			setError('Watermark is required');
			return;
		}
		if (!answerType) {
			setError('Answer Type is required');
			return;
		}

		setLoading(true);
		setError(null);

		try {
			// 1. Fetch Questions List
			const questionsPromise = axios.get(
				`${API_BASE_URL}/questionanswerbySubjectId`,
				{
					params: { subjectId: subjectCode },
					headers: { Authorization: `Bearer ${token}` },
					withCredentials: true,
				}
			);

			// 2. Fetch Answers
			const answersPromise = axios.get(`${API_BASE_URL}/allquestionreference`, {
				params: {
					subjectId: subjectCode,
					answerType: answerType,
					referenceType: 'CHATGPT',
				},
				headers: { Authorization: `Bearer ${token}` },
				withCredentials: true,
			});

			const [questionsRes, answersRes] = await Promise.all([
				questionsPromise,
				answersPromise,
			]);

			const questionsArray = questionsRes.data.success || [];
			const answersArray = answersRes.data.success || [];

			const questions = Array.isArray(questionsArray)
				? questionsArray.map((q: any) => ({
						question: q.questionReference || '',
						weightage: q.weightage || 0,
				  }))
				: [];

			// 3. Generate PDF
			// @ts-ignore
			const doc = new jsPDF({
				encryption: {
					userPermissions: ['accessibility'],
					userPassword: '',
					ownerPassword:
						'llb360_secure_' + Math.random().toString(36).substring(7),
				},
			});
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const margin = 15;
			let cursorY = margin;
			const contentWidth = pageWidth - margin * 2;

			// Helper: Draw Page Header
			const drawPageHeader = () => {
				const headerY = 15;
				const dateStr = new Date().toLocaleDateString('en-GB');

				// Logo
				if (myLogo) {
					doc.addImage(myLogo, 'PNG', headerY, headerY, 20, 20);
				}

				doc.setTextColor(0, 0, 0);

				// Line 1: LLB 360
				doc.setFontSize(18);
				doc.setFont('helvetica', 'bold');
				doc.text('LLB 360', 40, headerY + 8);

				// Line 2: Subject - Type
				doc.setFontSize(12);
				doc.setFont('helvetica', 'normal');
				const typeLabel =
					answerTypeOptions.find((o) => o.value === answerType)?.label ||
					answerType;
				doc.text(
					cleanTextForPdf(`${subjectName} - ${typeLabel}`),
					40,
					headerY + 16
				);

				// Date Right
				doc.setFontSize(10);
				doc.setTextColor(100, 100, 100);
				doc.text(`Exported on: ${dateStr}`, pageWidth - 15, headerY + 8, {
					align: 'right',
				});

				// Border Line
				const bottomY = headerY + 25;
				doc.setDrawColor(200, 200, 200);
				doc.setLineWidth(0.5);
				doc.line(headerY, bottomY, pageWidth - headerY, bottomY);

				doc.setTextColor(0, 0, 0); // Reset to black

				return bottomY + 10; // New cursorY
			};

			const checkPageBreak = (heightNeeded: number) => {
				if (cursorY + heightNeeded > pageHeight - margin) {
					doc.addPage();
					cursorY = drawPageHeader();
				}
			};

			// Initial Header for Page 1
			cursorY = drawPageHeader();

			// Questions List (Table Format)
			doc.setFontSize(12);
			const tableMargin = margin;
			const col1Width = 15; // Sl.No
			const col3Width = 25; // Weightage
			const col2Width = contentWidth - col1Width - col3Width; // Question
			const rowHeightPadding = 4; // Top + Bottom padding

			// Table State
			const questionLinkAreas: {
				qRef: string;
				page: number;
				x: number;
				y: number;
				w: number;
				h: number;
			}[] = [];
			const answerDestinations: Record<string, { page: number; y: number }> =
				{};

			// Helper to draw header
			const drawTableHeader = () => {
				doc.setFont('helvetica', 'bold');
				doc.setFillColor(240, 240, 240); // Light gray header
				doc.rect(tableMargin, cursorY, contentWidth, 8, 'F');
				doc.rect(tableMargin, cursorY, contentWidth, 8, 'S'); // Border

				// Vertical lines for header
				doc.line(
					tableMargin + col1Width,
					cursorY,
					tableMargin + col1Width,
					cursorY + 8
				);
				doc.line(
					tableMargin + col1Width + col2Width,
					cursorY,
					tableMargin + col1Width + col2Width,
					cursorY + 8
				);

				doc.text('Sl.No', tableMargin + 2, cursorY + 5.5);
				doc.text(
					'Question Reference',
					tableMargin + col1Width + 2,
					cursorY + 5.5
				);
				doc.text(
					'Weightage',
					tableMargin + col1Width + col2Width + 2,
					cursorY + 5.5
				);

				cursorY += 8;
			};

			drawTableHeader();

			doc.setFont('helvetica', 'normal');

			if (questions.length === 0) {
				doc.text('No questions found for this subject.', margin, cursorY + 5);
			} else {
				questions.forEach((q: any, index: number) => {
					const slNo = String(index + 1);
					const qText = cleanTextForPdf(q.question || '');
					const weightage = String(q.weightage || 0);

					// Text Wrapping
					const splitQ = doc.splitTextToSize(qText, col2Width - 4);
					const rowHeight = splitQ.length * 5 + rowHeightPadding;

					// Check page break
					if (cursorY + rowHeight > pageHeight - margin) {
						doc.addPage();
						cursorY = drawPageHeader();
						drawTableHeader();
						doc.setFont('helvetica', 'normal');
					}

					// Draw Row Background/Border
					doc.rect(tableMargin, cursorY, contentWidth, rowHeight); // Outer border

					// Vertical Lines
					doc.line(
						tableMargin + col1Width,
						cursorY,
						tableMargin + col1Width,
						cursorY + rowHeight
					);
					doc.line(
						tableMargin + col1Width + col2Width,
						cursorY,
						tableMargin + col1Width + col2Width,
						cursorY + rowHeight
					);

					// Content
					doc.text(slNo, tableMargin + 2, cursorY + 5);

					// Store coordinates for linking later
					// We link the Question Reference cell
					questionLinkAreas.push({
						qRef: qText.trim(),
						// @ts-ignore
						page: doc.internal.getCurrentPageInfo().pageNumber,
						x: tableMargin + col1Width,
						y: cursorY,
						w: col2Width,
						h: rowHeight,
					});

					doc.text(splitQ, tableMargin + col1Width + 2, cursorY + 5);
					doc.text(
						weightage,
						tableMargin + col1Width + col2Width + 2,
						cursorY + 5
					);

					cursorY += rowHeight;
				});
			}

			// --- Answers Page ---
			if (answersArray.length > 0) {
				doc.addPage();
				cursorY = drawPageHeader();

				doc.setFontSize(18);
				doc.setFont('helvetica', 'bold');
				doc.text('Answers', margin, cursorY);
				cursorY += 15;

				const printContent = (content: any, indent: number = 0) => {
					if (!content) return;

					doc.setFontSize(11);
					const safeIndent = margin + indent;
					const safeWidth = contentWidth - indent;

					if (typeof content === 'string') {
						const cleaned = cleanTextForPdf(content);
						const splitText = doc.splitTextToSize(cleaned, safeWidth);
						const height = splitText.length * 5;
						checkPageBreak(height);
						doc.setFont('helvetica', 'normal');
						doc.text(splitText, safeIndent, cursorY);
						cursorY += height + 2;
					} else if (Array.isArray(content)) {
						content.forEach((item) => {
							printContent(item, indent + 5);
						});
					} else if (typeof content === 'object') {
						Object.entries(content).forEach(([key, value]) => {
							checkPageBreak(7);
							doc.setFont('helvetica', 'bold');
							doc.text(
								`${cleanTextForPdf(formatKey(key))}:`,
								safeIndent,
								cursorY
							);
							cursorY += 5;
							printContent(value, indent + 5);
							cursorY += 2;
						});
					}
				};

				answersArray.forEach((ans: any, index: number) => {
					checkPageBreak(20);
					doc.setFontSize(14);
					doc.setFont('helvetica', 'bold');
					doc.setTextColor(0, 0, 150); // Dark blue for question
					const questionText = cleanTextForPdf(
						`Q${index + 1}: ${ans.questionReference || 'Question'}`
					);
					const splitQ = doc.splitTextToSize(questionText, contentWidth);
					const h = splitQ.length * 7;

					checkPageBreak(h);
					doc.text(splitQ, margin, cursorY);
					cursorY += h + 5;

					doc.setTextColor(0, 0, 0); // Reset color

					let parsedAnswer = null;
					try {
						parsedAnswer = deepNormalize(JSON.parse(ans.answer));
					} catch (e) {
						parsedAnswer = ans.answer;
					}

					// Reorder and Rename Keys based on Answer Type
					let contentToPrint = parsedAnswer;
					const mapping = FIELD_MAPPINGS[answerType];
					if (
						mapping &&
						parsedAnswer &&
						typeof parsedAnswer === 'object' &&
						!Array.isArray(parsedAnswer)
					) {
						const sorted: any = {};
						// Add mapped keys in order
						mapping.forEach(({ key, label, subMapping }) => {
							if (Object.prototype.hasOwnProperty.call(parsedAnswer, key)) {
								let val = parsedAnswer[key];
								if (
									subMapping &&
									val &&
									typeof val === 'object' &&
									!Array.isArray(val)
								) {
									const subSorted: any = {};
									subMapping.forEach((sub) => {
										if (Object.prototype.hasOwnProperty.call(val, sub.key)) {
											subSorted[sub.label] = val[sub.key];
										}
									});
									Object.keys(val).forEach((k) => {
										const isMapped = subMapping.some((m) => m.key === k);
										if (!isMapped) {
											subSorted[formatKey(k)] = val[k];
										}
									});
									val = subSorted;
								}
								sorted[label] = val;
							}
						});
						// Add remaining keys
						Object.keys(parsedAnswer).forEach((k) => {
							const isMapped = mapping.some((m) => m.key === k);
							if (!isMapped) {
								sorted[formatKey(k)] = parsedAnswer[k];
							}
						});
						contentToPrint = sorted;
					}

					printContent(contentToPrint);

					cursorY += 10;
					checkPageBreak(5);
					doc.setLineWidth(0.2);
					doc.setDrawColor(200, 200, 200);
					doc.line(margin, cursorY, pageWidth - margin, cursorY);
					cursorY += 10;
				});

				// Apply Links (Back-patching)
				questionLinkAreas.forEach((area) => {
					const dest = answerDestinations[area.qRef];
					if (dest) {
						doc.setPage(area.page);
						// Create link over the cell area
						doc.link(area.x, area.y, area.w, area.h, {
							pageNumber: dest.page,
							top: dest.y,
						});
					}
				});
			}

			// 4. Add Page Details (Watermark, Footer, Page Numbers)
			addPageDetails(doc, waterMark);

			// 5. Save
			const typeLabel =
				answerTypeOptions.find((o) => o.value === answerType)?.label ||
				answerType;
			doc.save(`${subjectName} (${typeLabel.replace(/\s+/g, '_')}).pdf`);
			toast.success('PDF exported successfully!');
			setWaterMark('');
			setAnswerType('');
			onClose();
		} catch (err: unknown) {
			console.error(err);
			if (axios.isAxiosError(err) && err.response?.status === 401) {
				toast.error('Session expired. Please log in again.');
				clearSession();
			} else {
				toast.error('Failed to export PDF. Please try again.');
				setError('Failed to fetch data or generate PDF.');
			}
		} finally {
			setLoading(false);
		}
	};

	if (!showModal) return null;

	return (
		<div className='modalSection'>
			<div className='modalContainer'>
				<div className='flex justify-between items-center gap-2 border-b pb-1 border-gray-200 mb-2'>
					<h2 className='text-base font-semibold'>
						Export <b>{subjectName}</b> as PDF
					</h2>
					<button
						onClick={onClose}
						className='modalCloseButton'>
						<IoClose size={20} />
					</button>
				</div>

				{error && <p className='text-red-500 text-left mb-2'>{error}</p>}

				<div className='mt-6 mb-3'>
					<form
						onSubmit={handleExport}
						className='space-y-4 w-full'>
						<TextInput
							id='waterMark'
							name='waterMark'
							label='Water Mark'
							type='text'
							value={waterMark}
							onChange={(e) => {
								setWaterMark(e.target.value);
								if (error) setError(null);
							}}
							placeholder='Enter Water Mark'
							required
							error={!!error && error.includes('Watermark')}
							errorMessage={error && error.includes('Watermark') ? error : ''}
							maxLength={8}
						/>
						<p className='text-xs text-gray-500'>Maximum 8 characters</p>
						<SelectInput
							name='answerType'
							label='Answer Type'
							value={answerType}
							options={answerTypeOptions}
							onChange={(value) => {
								setAnswerType(value);
								if (error) setError(null);
							}}
							placeholder='Select Answer Type'
							required
							error={!!error && error.includes('Answer Type')}
							errorMessage={error && error.includes('Answer Type') ? error : ''}
						/>
						<AuthButton
							title='Export'
							loadingTitle='Exporting...'
							loading={loading}
							disabled={loading || !waterMark.trim() || !answerType}
							icon={IoDownload}
						/>
					</form>
				</div>
			</div>
		</div>
	);
};

export default ExportAsPDFModal;

const answerTypeOptions = [
	{ value: 'CP', label: 'Concepts' },
	{ value: 'SN', label: 'Short Notes' },
	{ value: 'EN', label: 'Essay Notes' },
	{ value: 'CS', label: 'Case Study' },
];
